import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { useModalEffects } from '@/shared/hooks/use-modal-effects';

interface Props {
	open: boolean;
	/** Disables the resend button while a resend is in flight, so one click sends one message. */
	isResending?: boolean;
	/** False when the patient has no number on file — the resend button is disabled rather than absent. */
	canResend?: boolean;
	onResend: () => void;
	onClose: () => void;
}

/**
 * Tells the clinician the app-installation guide never reached the patient, and offers to send it
 * again (FEATURE_014 (Patient Registration)).
 *
 * <p>Shown after a registration that succeeded: the patient is saved either way, and only the text
 * message failed — so this modal reports a delivery problem, never a registration one. That is why it
 * appears once the registration form has already closed.
 */
export function InstallSmsFailModal({
	open,
	isResending = false,
	canResend = true,
	onResend,
	onClose,
}: Props) {
	const { t } = useTranslation(['medical', 'common']);
	const modalRef = useRef<HTMLDivElement>(null);

	useModalEffects({ open, onClose, modalRef });

	if (!open) return null;

	return (
		<div
			ref={modalRef}
			className='modal-overlay modal-overlay--visible'
			role='dialog'
			aria-modal='true'
		>
			<div className='modal'>
				<div className='modal__header'>
					<h3 className='modal__title text-red-600'>
						{t('medical:patients.installSmsFailModal.title')}
					</h3>
					<button
						type='button'
						className='modal__close'
						onClick={onClose}
						aria-label={t('common:actions.close')}
					>
						<X size={20} />
					</button>
				</div>

				<div className='modal__body'>
					<p className='text-sm text-gray-700 leading-relaxed'>
						{t('medical:patients.installSmsFailModal.body')}
					</p>
				</div>

				<div className='modal__footer'>
					<Button type='button' variant='secondary' onClick={onClose}>
						{t('medical:patients.installSmsFailModal.close')}
					</Button>
					<Button
						type='button'
						onClick={onResend}
						disabled={isResending || !canResend}
					>
						{t('medical:patients.installSmsFailModal.resend')}
					</Button>
				</div>
			</div>
		</div>
	);
}
